import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  CalendarRange,
  ChartLine,
  Download,
  FileSpreadsheet,
  Filter,
  PieChart as PieChartIcon,
  RotateCcw,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UserSectionSidebar from '../components/UserSectionSidebar';
import DateTimePicker from '../components/DateTimePicker';
import ThemedMultiSelect from '../components/ThemedMultiSelect';
import ThemedSelect from '../components/ThemedSelect';
import LoadingIndicator from '../components/LoadingIndicator';
import { useNotification } from '../components/NotificationProvider';
import { useTranslations } from '../translations';
import type {
  ReportBreakdown,
  ReportGranularity,
  ReportDateField,
  SellerOrderLocationOption,
  SellerOrderProductOption,
  SellerOrderReport,
  SellerOrderStatus,
} from '../services/api.types';
import {
  fetchSellerOrderProductOptions,
  fetchSellerOrderLocationOptions,
  fetchSellerOrderReport,
} from '../services/seller-order.api';
import type { SellerOrderReportFilters } from '../services/seller-order.api';
import { exportReportCsv, exportReportPdf } from '../utils/sellerOrderExport';
import type { AccountPageProps } from './accountPage.types';
import { formatUnitType } from '../utils/unitType';
import { localDateValue, toEndOfDayInstant, toStartOfDayInstant } from '../utils/dateRange';
import './Orders.css';
import './OrderReport.css';

type ChartType = 'BAR' | 'PIE' | 'LINE';
const SellerOrderReportChart = lazy(() => import('../components/SellerOrderReportChart'));

const initialEnd = new Date();
const initialStart = new Date(initialEnd.getFullYear(), initialEnd.getMonth(), 1, 0, 0);
const initialRange = {
  from: localDateValue(initialStart),
  to: localDateValue(initialEnd),
};

const money = (value: number) => new Intl.NumberFormat('ro-RO', {
  maximumFractionDigits: 2,
}).format(value || 0);
const quantity = (value: number) => new Intl.NumberFormat('ro-RO', {
  maximumFractionDigits: 3,
}).format(value || 0);

const OrderReport: React.FC<AccountPageProps> = ({
  isLoggedIn,
  isAuthChecking,
  isAdmin = false,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToAdministration,
  onLogout,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact,
}) => {
  const t = useTranslations();
  const copy = t.orderReport;
  const { showNotification } = useNotification();
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [granularity, setGranularity] = useState<ReportGranularity>('DAY');
  const [dateField, setDateField] = useState<ReportDateField>('ORDER_DATE');
  const [breakdown, setBreakdown] = useState<ReportBreakdown>('TOTAL');
  const [locationId, setLocationId] = useState('');
  const [orderLocationId, setOrderLocationId] = useState('');
  const [productId, setProductId] = useState('');
  const [statuses, setStatuses] = useState<SellerOrderStatus[]>([]);
  const [locations, setLocations] = useState<SellerOrderLocationOption[]>([]);
  const [products, setProducts] = useState<SellerOrderProductOption[]>([]);
  const [report, setReport] = useState<SellerOrderReport | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [chartType, setChartType] = useState<ChartType>('BAR');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const filters = useMemo(() => ({
    from: from ? toStartOfDayInstant(from) : '',
    to: to ? toEndOfDayInstant(to) : '',
    granularity,
    dateField,
    breakdown,
    statuses,
    locationId: locationId || undefined,
    orderLocationId: orderLocationId || undefined,
    productId: productId || undefined,
  }), [breakdown, dateField, from, granularity, locationId, orderLocationId, productId, statuses, to]);

  const generate = useCallback(async (filtersToApply: SellerOrderReportFilters = filters) => {
    if (!from || !to || from > to) {
      showNotification(copy.invalidInterval, 'error');
      return;
    }
    setHasGenerated(true);
    setLoading(true);
    try {
      setReport(await fetchSellerOrderReport(filtersToApply));
    } catch (error) {
      console.error('Could not generate seller order report', error);
      showNotification(copy.loadError, 'error');
    } finally {
      setLoading(false);
    }
  }, [copy.invalidInterval, copy.loadError, filters, from, showNotification, to]);

  const handleApplyFilters = () => {
    if (from && to && from <= to) {
      setShowMobileFilters(false);
    }
    void generate();
  };

  const handleResetFilters = () => {
    if (!from || !to || from > to) {
      showNotification(copy.invalidInterval, 'error');
      return;
    }

    const resetFilters: SellerOrderReportFilters = {
      from: toStartOfDayInstant(from),
      to: toEndOfDayInstant(to),
      granularity: 'DAY',
      dateField: 'ORDER_DATE',
      breakdown: 'TOTAL',
      statuses: [],
      locationId: undefined,
      orderLocationId: undefined,
      productId: undefined,
    };
    setGranularity('DAY');
    setDateField('ORDER_DATE');
    setBreakdown('TOTAL');
    setStatuses([]);
    setLocationId('');
    setOrderLocationId('');
    setProductId('');
    void generate(resetFilters);
  };

  useEffect(() => {
    if (isAuthChecking) return;
    if (!isLoggedIn || isAdmin) {
      onNavigateToLogin();
      return;
    }
    let active = true;
    setLoading(true);
    Promise.all([
      fetchSellerOrderLocationOptions(),
      fetchSellerOrderProductOptions(),
    ])
      .then(([locationOptions, options]) => {
        if (!active) return;
        setLocations(locationOptions);
        setProducts(options);
      })
      .catch((error) => {
        console.error('Could not load seller order report', error);
        if (active) showNotification(copy.loadError, 'error');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [copy.loadError, isAdmin, isAuthChecking, isLoggedIn, onNavigateToLogin, showNotification]);

  const runExport = async (format: 'CSV' | 'PDF') => {
    if (!report) return;
    setExporting(true);
    try {
      const result = format === 'CSV'
        ? await exportReportCsv(report)
        : await exportReportPdf(report);
      if (result === 'downloaded') {
        showNotification(copy.exportStarted.replace('{format}', format), 'success');
      }
    } catch (error) {
      console.error('Could not export seller order report', error);
      showNotification(copy.exportError, 'error');
    } finally {
      setExporting(false);
    }
  };

  const summary = report?.summary;
  const locationOptions = useMemo(() => Array.from(
    new Map(locations.map((location) => [
      location.locationId,
      { value: location.locationId, label: `${location.locationName}, ${location.countyName}` },
    ])).values(),
  ).sort((first, second) => first.label.localeCompare(second.label, 'ro')), [locations]);
  const orderLocationOptions = useMemo(() => locations
    .filter((location) => location.locationId === locationId)
    .map((location) => ({
      value: location.id,
      label: `${location.name}${location.addressDetails ? ` · ${location.addressDetails}` : ''}${location.active ? '' : ' (Arhivat)'}`,
    })), [locationId, locations]);
  const activeFilterCount = useMemo(() => [
    granularity !== 'DAY',
    dateField !== 'ORDER_DATE',
    breakdown !== 'TOTAL',
    statuses.length > 0,
    Boolean(locationId),
    Boolean(orderLocationId),
    Boolean(productId),
  ].filter(Boolean).length, [breakdown, dateField, granularity, locationId, orderLocationId, productId, statuses]);
  const chartData = useMemo(() => report?.rows.map((row) => ({
    name: [row.periodLabel, row.locationName, row.productTitle].filter(Boolean).join(' · '),
    totalAmount: row.expectedAmount + row.cancelledAmount,
    deliveredAmount: row.deliveredAmount,
    cancelledAmount: row.cancelledAmount,
  })) ?? [], [report]);

  useEffect(() => {
    if (!showMobileFilters) return;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowMobileFilters(false);
    };
    const closeOnDesktopResize = () => {
      if (window.innerWidth > 768) setShowMobileFilters(false);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', closeOnDesktopResize);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', closeOnDesktopResize);
    };
  }, [showMobileFilters]);

  return (
    <div className="order-report-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onAdministrationClick={onNavigateToAdministration}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />
      <div className="order-report-container">
        <UserSectionSidebar
          activeItem="orderReport"
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={() => onNavigateToMessages?.()}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onSettingsClick={onNavigateToSettings}
        />
        <main className="order-report-main">
          <header className="order-report-header">
            <div className="orders-page-heading">
              <span className="orders-page-eyebrow">
                <BarChart3 size={14} aria-hidden />
                {copy.eyebrow}
              </span>
              <h1>{copy.title}</h1>
              <p>{copy.subtitle}</p>
            </div>
            <div className="order-report-overview" aria-hidden="true">
              <span className="order-report-overview-icon"><BarChart3 size={21} /></span>
              <div>
                <strong>{copy.overviewTitle}</strong>
                <small>{copy.overviewLabel}</small>
              </div>
            </div>
          </header>

          <section className="seller-order-date-range" aria-label={copy.intervalLabel}>
            <div className="orders-section-heading">
              <span className="orders-section-icon" aria-hidden><CalendarRange size={20} /></span>
              <div>
                <h2>{copy.intervalLabel}</h2>
                <p>{copy.intervalHint}</p>
              </div>
            </div>
            <div className="seller-order-date-field">
              <span>{copy.from}</span>
              <DateTimePicker value={from} onChange={setFrom} ariaLabel={copy.from} clearLabel={copy.clearFrom} dateOnly />
            </div>
            <div className="seller-order-date-field">
              <span>{copy.to}</span>
              <DateTimePicker value={to} onChange={setTo} ariaLabel={copy.to} clearLabel={copy.clearTo} min={from || undefined} dateOnly />
            </div>
            <button
              type="button"
              className="seller-order-date-submit"
              onClick={() => void generate()}
              disabled={loading}
            >
              {copy.generate}
            </button>
          </section>

          <button
            type="button"
            className="report-mobile-filters-button"
            aria-controls="order-report-filters"
            aria-expanded={showMobileFilters}
            onClick={() => setShowMobileFilters(true)}
          >
            <SlidersHorizontal size={18} aria-hidden />
            <span>{copy.filters}</span>
            {activeFilterCount > 0 && (
              <span className="report-mobile-filter-count" aria-label={copy.activeFilters.replace('{count}', activeFilterCount.toString())}>
                {activeFilterCount}
              </span>
            )}
          </button>

          {showMobileFilters && (
            <button
              type="button"
              className="report-mobile-filters-overlay"
              aria-label={copy.closeFilters}
              onClick={() => setShowMobileFilters(false)}
            />
          )}

          <section
            id="order-report-filters"
            className={`report-filters ${showMobileFilters ? 'report-mobile-visible' : ''}`}
            aria-label={copy.filtersTitle}
          >
            <div className="report-mobile-filters-header">
              <h2>{copy.filters}</h2>
              <button
                type="button"
                aria-label={copy.closeFilters}
                onClick={() => setShowMobileFilters(false)}
              >
                <X size={20} aria-hidden />
              </button>
            </div>
            <div className="report-filter-heading">
              <span className="orders-section-icon" aria-hidden><SlidersHorizontal size={19} /></span>
              <div>
                <h2>{copy.filtersTitle}</h2>
                <p>{copy.filtersHint}</p>
              </div>
            </div>
            <div className="report-filter-grid">
              <div className="report-field">
                <span>{copy.granularity}</span>
                <ThemedSelect
                  value={granularity}
                  onChange={setGranularity}
                  ariaLabel={copy.granularity}
                  options={[
                    { value: 'DAY', label: copy.byDay },
                    { value: 'WEEK', label: copy.byWeek },
                    { value: 'MONTH', label: copy.byMonth },
                  ]}
                />
              </div>
              <div className="report-field">
                <span>{copy.statusFilter}</span>
                <ThemedMultiSelect
                  values={statuses}
                  onChange={setStatuses}
                  ariaLabel={copy.statusFilter}
                  allLabel={copy.allStatuses}
                  options={[
                    { value: 'PENDING', label: copy.pending },
                    { value: 'DELIVERED', label: copy.delivered },
                    { value: 'CANCELLED', label: copy.cancelled },
                  ]}
                />
              </div>
              <div className="report-field">
                <span>{copy.breakdown}</span>
                <ThemedSelect
                  value={breakdown}
                  onChange={setBreakdown}
                  ariaLabel={copy.breakdown}
                  options={[
                    { value: 'TOTAL', label: copy.total },
                    { value: 'LOCATION', label: copy.byLocation },
                    { value: 'PRODUCT', label: copy.byProduct },
                    { value: 'LOCATION_PRODUCT', label: copy.byLocationProduct },
                  ]}
                />
              </div>
              <div className="report-field">
                <span>{copy.dateField}</span>
                <ThemedSelect
                  value={dateField}
                  onChange={setDateField}
                  ariaLabel={copy.dateField}
                  options={[
                    { value: 'ORDER_DATE', label: copy.orderDate },
                    { value: 'DELIVERY_DATE', label: copy.deliveryDate },
                  ]}
                />
              </div>
              <div className="report-field">
                <span>{copy.mainLocation}</span>
                <ThemedSelect
                  value={locationId}
                  onChange={(value) => {
                    setLocationId(value);
                    setOrderLocationId('');
                  }}
                  ariaLabel={copy.mainLocation}
                  options={[{ value: '', label: copy.allLocations }, ...locationOptions]}
                />
              </div>
              <div className="report-field">
                <span>{copy.deliveryPoint}</span>
                <ThemedSelect
                  value={orderLocationId}
                  onChange={setOrderLocationId}
                  ariaLabel={copy.deliveryPoint}
                  disabled={!locationId}
                  options={locationId
                    ? [{ value: '', label: copy.allDeliveryPoints }, ...orderLocationOptions]
                    : [{ value: '', label: copy.selectMainLocationFirst }]}
                />
              </div>
              <div className="report-field">
                <span>{copy.product}</span>
                <ThemedSelect
                  value={productId}
                  onChange={setProductId}
                  ariaLabel={copy.product}
                  options={[
                    { value: '', label: copy.allProducts },
                    ...products.map((product) => ({ value: product.id, label: product.title })),
                  ]}
                />
              </div>
            </div>
            <div className="report-mobile-filter-actions">
              <button
                type="button"
                className="report-mobile-filter-apply"
                disabled={!from || !to || loading}
                onClick={handleApplyFilters}
              >
                <Filter size={17} aria-hidden />
                {copy.applyFilters}
              </button>
              <button
                type="button"
                className="report-mobile-filter-reset"
                disabled={activeFilterCount === 0 || loading}
                onClick={handleResetFilters}
              >
                <RotateCcw size={17} aria-hidden />
                {copy.resetFilters}
              </button>
            </div>
          </section>

          <div className="report-export-actions" role="group" aria-label={copy.exportReport}>
            <div className="orders-export-actions">
              <button type="button" disabled={exporting || !report} onClick={() => runExport('CSV')}><FileSpreadsheet size={17} aria-hidden /> CSV</button>
              <button type="button" disabled={exporting || !report} onClick={() => runExport('PDF')}><Download size={17} aria-hidden /> PDF</button>
            </div>
          </div>

          {summary && (
            <section className="orders-summary report-summary" aria-label="Sumar raport">
              <div><span>{copy.orders}</span><strong>{summary.orderCount}</strong></div>
              <div><span>{copy.expected}</span><strong>{money(summary.expectedAmount)} lei</strong></div>
              <div><span>{copy.deliveredAmount}</span><strong>{money(summary.deliveredAmount)} lei</strong></div>
              <div><span>{copy.cancelledAmount}</span><strong>{money(summary.cancelledAmount)} lei</strong></div>
            </section>
          )}

          {report && report.rows.length > 0 && (
            <section className="report-chart-section" aria-labelledby="report-chart-title">
              <header className="report-chart-header">
                <div>
                  <h2 id="report-chart-title">{copy.visualization}</h2>
                  <span>{copy.chartValues}</span>
                </div>
                <div className="report-chart-controls">
                  <div className="report-chart-toggle" role="group" aria-label={copy.chartType}>
                    <button type="button" className={chartType === 'BAR' ? 'active' : ''} aria-pressed={chartType === 'BAR'} aria-label={copy.barChart} title={copy.barChart} onClick={() => setChartType('BAR')}>
                      <BarChart3 size={18} aria-hidden />
                    </button>
                    <button type="button" className={chartType === 'PIE' ? 'active' : ''} aria-pressed={chartType === 'PIE'} aria-label={copy.pieChart} title={copy.pieChart} onClick={() => setChartType('PIE')}>
                      <PieChartIcon size={18} aria-hidden />
                    </button>
                    <button type="button" className={chartType === 'LINE' ? 'active' : ''} aria-pressed={chartType === 'LINE'} aria-label={copy.lineChart} title={copy.lineChart} onClick={() => setChartType('LINE')}>
                      <ChartLine size={18} aria-hidden />
                    </button>
                  </div>
                </div>
              </header>
              <div className="report-chart-canvas">
                <Suspense fallback={<LoadingIndicator message={copy.loading} className="orders-state" />}>
                  <SellerOrderReportChart
                    type={chartType}
                    data={chartData}
                    labels={{
                      total: copy.totalAmountMetric,
                      delivered: copy.deliveredOrderMetric,
                      cancelled: copy.cancelledOrderMetric,
                    }}
                  />
                </Suspense>
              </div>
            </section>
          )}

          {loading ? (
            <LoadingIndicator message={copy.loading} className="orders-state" />
          ) : !hasGenerated ? (
            <div className="orders-state orders-empty">{copy.selectInterval}</div>
          ) : !report || report.rows.length === 0 ? (
            <div className="orders-state orders-empty">{copy.empty}</div>
          ) : (
            <div className="report-table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>{copy.period}</th><th>{copy.location}</th><th>{copy.product}</th><th>{copy.orders}</th>
                    <th>{copy.pending}</th><th>{copy.delivered}</th><th>{copy.cancelled}</th><th>{copy.quantity}</th><th>{copy.unit}</th>
                    <th>{copy.expected}</th><th>{copy.deliveredAmount}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((row, index) => (
                    <tr key={`${row.periodStart}-${row.locationId}-${row.productId}-${index}`}>
                      <td data-label={copy.period}><strong>{row.periodLabel}</strong></td>
                      <td data-label={copy.location}>{row.locationName || '—'}</td>
                      <td data-label={copy.product}>{row.productTitle || '—'}</td>
                      <td data-label={copy.orders}>{row.orderCount}</td>
                      <td data-label={copy.pending}>{row.pendingCount}</td>
                      <td data-label={copy.delivered}>{row.deliveredCount}</td>
                      <td data-label={copy.cancelled}>{row.cancelledCount}</td>
                      <td data-label={copy.quantity}>{row.unitType ? quantity(row.activeQuantity) : '—'}</td>
                      <td data-label={copy.unit}>{row.unitType ? formatUnitType(row.unitType) : '—'}</td>
                      <td data-label={copy.expected}><strong>{money(row.expectedAmount)} lei</strong></td>
                      <td data-label={copy.deliveredAmount}>{money(row.deliveredAmount)} lei</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
  );
};

export default OrderReport;
